import { produce } from 'immer';
import _ from 'lodash';
import { MutableRefObject, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import AdventPlaceHolder from '../../../../../common/assets/images/advent-placeholder.jpg';
import Icons from '../../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { ProductCardModel } from '../../../models/Product';
import { resources } from '../../../resources/resources';
import { wishlistService } from '../../../services/wishlist';

import {
  formatDate,
  getDiscountPercentage,
  showErrorToast,
} from '../../../../../common/global/helpers';
import Button from '../../../../../common/widgets/Button';
import { config } from '../../../config';
import { BuyerTypes, TopLevelCategories } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';

interface ProductListItemProps {
  product: ProductCardModel;
  onProductClick?: () => void;
}

export default function ProductListItem(props: ProductListItemProps) {
  const { product, onProductClick } = props;
  const { t: translate } = useTranslation();
  const { categoryId } = useParams();
  const imgagePath = rewriteImageUrl(product.imagePath);
  const secretImagePath = rewriteImageUrl(product.secretImagePath);
  const location = useLocation();

  const discount = getDiscountPercentage(
    product.price.defaultPrice,
    product.price.discountedPrice ?? 0,
  );
  const isProduct: boolean = !!product.productId && !product.brandId;

  const [isFavorite, setIsFavorite] = useState<boolean>(product.isFavorite);
  const [isFavoriteActionRunning, setIsFavoriteActionRunning] = useState(false);

  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const addFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const removeFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const isFavoriteActionRef = useRef(false);

  const addToFavorties = async (e: MouseEvent) => {
    if (!product.productId) {
      return;
    }
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;
    setIsFavoriteActionRunning(true);

    setIsFavorite(true);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount += 1;
      }),
    );

    addFavoriteAbortControllerRef.current = new AbortController();
    removeFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.addProduct(product.productId, {
        signal: addFavoriteAbortControllerRef.current.signal,
      });
      if (addFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        setIsFavorite(false);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount -= 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };

  const removeFromFavorties = async (e: MouseEvent) => {
    if (!product.productId) {
      return;
    }
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;
    setIsFavoriteActionRunning(true);

    setIsFavorite(false);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount -= 1;
      }),
    );

    removeFavoriteAbortControllerRef.current = new AbortController();
    addFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.removeProduct(product.productId, {
        signal: removeFavoriteAbortControllerRef.current.signal,
      });
      if (removeFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        setIsFavorite(true);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount += 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };

  const getNavigationLink = (): string => {
    if (product.productId) {
      return `/products/${product.productId}`;
    }

    if (categoryId) {
      return `${location.pathname}?brand=${product.brandId}`;
    }

    const productCategoryPath = `${product.path[1]}?brand=${product.brandId}`;

    return product.path[0] === TopLevelCategories.FlexibleBenefits
      ? `/flexible-benefits/${productCategoryPath}`
      : `/club-benefits/${productCategoryPath}`;
  };

  const hasPromo =
    (product.price.discountedPrice !== null &&
      product.price.discountedPrice != product.price.defaultPrice) ||
    product.buyerType !== null;

  return !product.isSecret ? (
    <Link
      className={`card product-card brand`}
      to={getNavigationLink()}
      onClick={onProductClick}
      draggable={false}
    >
      {/* change routing*/}
      <div className="card-img">
        <img src={imgagePath} alt={product.name} draggable={false} />

        {hasPromo && !product.hidePriceAndTaxes && (
          <div className="card-promo">
            {product.price.discountedPrice !== null &&
              product.price.discountedPrice != product.price.defaultPrice &&
              (isProduct ? (
                <div>{discount}%</div>
              ) : (
                <div>{translate(resources.products.discounts)}</div>
              ))}
            {product.buyerType !== null && (
              <div className="type">
                {product.buyerType === BuyerTypes.Dependent
                  ? translate(resources.products.buyerType.dependents)
                  : translate(resources.products.buyerType.employee)}
              </div>
            )}
          </div>
        )}

        {isProduct && (
          <>
            {!isFavorite && (
              <Button
                className="btn btn-card-favorite"
                onClick={(e: any) => addToFavorties(e)}
                disabled={isFavoriteActionRunning}
              >
                <span className="btn-icon">
                  <Icons.CardFavorite />
                </span>
              </Button>
            )}
            {isFavorite && (
              <Button
                className="btn btn-card-favorite active"
                onClick={(e: any) => removeFromFavorties(e)}
              >
                <span className="btn-icon">
                  <Icons.CardFavorite />
                </span>
              </Button>
            )}
          </>
        )}
      </div>
      <div className="card-details">
        <div className="card-title">{_.unescape(product?.name)}</div>
        <div className="card-value">
          <ProductValue product={product} isProduct={isProduct} />
        </div>
      </div>
    </Link>
  ) : (
    <div className="card product-card">
      <div className="card-img">
        {product.secretImagePath ? (
          <img src={secretImagePath} alt={product.name} />
        ) : (
          <img src={AdventPlaceHolder} alt={product.name} />
        )}
      </div>

      <div className="card-details">
        <div className="card-title">
          {translate(resources.products.secretMessage, {
            0: formatDate(product.adventDate?.toString()),
          })}
        </div>
      </div>
    </div>
  );
}

type ProductValueProps = {
  product: ProductCardModel;
  isProduct: boolean;
};

const ProductValue = (props: ProductValueProps) => {
  const { t: translate } = useTranslation();
  const { product, isProduct } = props;

  if (product.cashBackValue) {
    return (
      <>
        {product.cashBackValue}{' '}
        {translate(resources.products.cashbackDiscountSufix).toLowerCase()}
      </>
    );
  }

  if (product.hidePriceAndTaxes) {
    return <></>;
  }

  if (
    product.price.priceRanges &&
    product.price.priceRanges[0] !== product.price.priceRanges[1]
  ) {
    return (
      <>
        {_.first(product.price.priceRanges)}
        <sup>{config.currency}</sup>
        {' - '}
        {_.last(product.price.priceRanges)}
        <sup>{config.currency}</sup>
      </>
    );
  }

  if (product.price.discountedPrice !== 0 && product.price.defaultPrice !== 0) {
    return (
      <>
        {product.price.discountedPrice ?? product.price.defaultPrice}
        <sup>{config.currency}</sup>

        <div className="card-old-value">
          {isProduct &&
          product.price.discountedPrice &&
          product.price.discountedPrice !== product.price.defaultPrice ? (
            <>
              <span>{product.price.defaultPrice}</span>
              <sup>{config.currency}</sup>
            </>
          ) : (
            <></>
          )}
        </div>
      </>
    );
  }

  return <></>;
};
